import { HttpContext, HttpContextToken, HttpParams, httpResource, HttpResourceRef } from '@angular/common/http';
import { Injector } from '@angular/core';

/**
 * HTTP-statusser som KALDEREN håndterer inline (fx cfg-table's fejltilstand med "Prøv igen").
 * {@link GlobalErrorInterceptor} viser ikke disse via ErrorService, men kaster altid videre.
 *
 * Kontrakt: global fejlhåndtering er slået TIL som standard; opt-out er eksplicit og selektiv.
 * En glemt opt-out giver en dublet-besked (synlig, harmløs); en glemt opt-in ville sluge fejlen
 * (farlig) — derfor vinder den sikre fejltilstand. 401/403 må ALDRIG stå på listen: login-redirect
 * og den blokerende modal skal forblive globale.
 */
export const HANDLED_ERROR_STATUSES = new HttpContextToken<readonly number[]>(() => []);

/** Alt hvad en tabel selv viser inline. 0 = transportfejl. 401/403 bevidst udeladt. */
export const TABLE_DEFAULT_HANDLED_STATUSES: readonly number[] = [0, 400, 404, 408, 409, 422, 500, 502, 503, 504];

export interface TableResourceRequest {
  url: string;
  params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>;
}

export interface TableResourceOptions<TResult, TRaw> {
  /** Map/normalisér det rå svar (fx objectType → displayObjectType). */
  map?: (raw: TRaw) => TResult[];
  /** Overstyr opt-out-listen; default er {@link TABLE_DEFAULT_HANDLED_STATUSES}. */
  handledStatuses?: readonly number[];
  injector?: Injector;
}

/**
 * `httpResource` preconfigured for cfg-table: GET, `withCredentials` (parity with HttpService)
 * and the {@link HANDLED_ERROR_STATUSES} context baked in, so consumers get the correct
 * error contract by default. Return `undefined` from `request` to idle (e.g. no department yet).
 */
export function tableResource<TResult, TRaw = TResult[]>(
  request: () => string | TableResourceRequest | undefined,
  options?: TableResourceOptions<TResult, TRaw>,
): HttpResourceRef<TResult[] | undefined> {
  const statuses = options?.handledStatuses ?? TABLE_DEFAULT_HANDLED_STATUSES;
  const context = new HttpContext().set(HANDLED_ERROR_STATUSES, statuses);
  return httpResource<TResult[]>(
    () => {
      const req = request();
      if (req === undefined) {
        return undefined;
      }
      const base = typeof req === 'string' ? { url: req } : req;
      return { ...base, method: 'GET', withCredentials: true, context };
    },
    {
      parse: options?.map as ((raw: unknown) => TResult[]) | undefined,
      injector: options?.injector,
    },
  );
}
